import type { AutomationType } from '@playbook-technologies/common-enums';
import * as Sentry from '@sentry/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useRef, useState } from 'react';

import { toAsync } from '@client/new/helpers/reactQuery';
import type { Async } from '@client/new/helpers/types';

import { isKnownAutomationType } from '../automation-copy';
import {
  CreatorAutomationItem,
  CreatorAutomationResponse,
  UpdateAutomationConfigRequest,
  UpdateCreatorAutomationRequest,
  getCreatorAutomations,
  updateCreatorAutomation,
} from '../automations.api';
import { serverErrorMessage } from '../automations.errors';
import { automationKeys } from '../automations.keys';

export type AutomationsModel = {
  automations: CreatorAutomationItem[];
  pending: ReadonlySet<AutomationType>;
  errors: Partial<Record<AutomationType, AutomationsUpdateError>>;
  statuses: Partial<Record<AutomationType, AutomationsUpdateStatus>>;
  retry: () => Promise<void>;
  update: (
    automationType: AutomationType,
    enabled: boolean,
    config?: UpdateAutomationConfigRequest,
  ) => Promise<void>;
  saveConfig: (
    automationType: AutomationType,
    config: UpdateAutomationConfigRequest,
  ) => Promise<void>;
};

export type AutomationsRequestErrorKind =
  | 'unauthorized'
  | 'forbidden'
  | 'network';

/**
 * `rejected` is creator-api's 400 with a business reason (fail-closed
 * enable gate, config validation). `message` is the server's text and the
 * only thing worth showing: retrying the same request cannot succeed.
 * The other kinds carry `intendedEnabled` when a toggle failed, so the View
 * can offer a retry with the same intent.
 */
export type AutomationsUpdateError =
  | { kind: 'rejected'; message: string; onEnable: boolean }
  | { kind: AutomationsRequestErrorKind; intendedEnabled?: boolean };

export type AutomationsUpdateStatus = { kind: 'saving' | 'saved' };

export type AutomationsLoadError = {
  kind: AutomationsRequestErrorKind | 'contract';
  retry: () => Promise<void>;
};

class AutomationsContractError extends Error {}

const toKnownAutomation = (
  raw: CreatorAutomationResponse,
): CreatorAutomationItem | null => {
  const automationType = raw.automationType;
  if (!isKnownAutomationType(automationType)) {
    Sentry.captureMessage(
      `Unsupported creator automation type: ${automationType}`,
    );
    return null;
  }
  return { ...raw, automationType };
};

const toKnownAutomations = (
  response: CreatorAutomationResponse[],
): CreatorAutomationItem[] =>
  response.flatMap((raw) => {
    const item = toKnownAutomation(raw);
    return item ? [item] : [];
  });

const statusOf = (error: unknown): number | undefined =>
  isAxiosError(error) ? error.response?.status : undefined;

export const errorKindOf = (error: unknown): AutomationsRequestErrorKind => {
  switch (statusOf(error)) {
    case 401:
      return 'unauthorized';
    case 403:
      return 'forbidden';
    default:
      return 'network';
  }
};

const toUpdateError = (
  error: unknown,
  intendedEnabled: boolean | undefined,
): AutomationsUpdateError => {
  const message = serverErrorMessage(error);
  if (statusOf(error) === 400 && message) {
    return { kind: 'rejected', message, onEnable: intendedEnabled === true };
  }
  return { kind: errorKindOf(error), intendedEnabled };
};

const loadErrorKindOf = (error: unknown): AutomationsLoadError['kind'] =>
  error instanceof AutomationsContractError ? 'contract' : errorKindOf(error);

const withEnabled = (
  automations: CreatorAutomationItem[] | undefined,
  automationType: AutomationType,
  enabled: boolean,
): CreatorAutomationItem[] =>
  (automations ?? []).map((item) =>
    item.automationType === automationType ? { ...item, enabled } : item,
  );

const withRow = (
  automations: CreatorAutomationItem[] | undefined,
  row: CreatorAutomationItem,
): CreatorAutomationItem[] =>
  (automations ?? []).map((item) =>
    item.automationType === row.automationType ? row : item,
  );

type PatchVariables = {
  automationType: AutomationType;
  body: UpdateCreatorAutomationRequest;
};

export const useAutomationsModel = (
  creatorId: number,
): Async<AutomationsModel, AutomationsLoadError> => {
  const queryClient = useQueryClient();
  const queryKey = automationKeys.list(creatorId);

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const known = toKnownAutomations(await getCreatorAutomations(creatorId));
      if (known.length === 0) {
        throw new AutomationsContractError('No supported automation types');
      }
      return known;
    },
    retry: false,
  });

  const [pending, setPending] = useState<ReadonlySet<AutomationType>>(
    new Set(),
  );
  const pendingRef = useRef<ReadonlySet<AutomationType>>(new Set());
  const [errors, setErrors] = useState<
    Partial<Record<AutomationType, AutomationsUpdateError>>
  >({});
  const [statuses, setStatuses] = useState<
    Partial<Record<AutomationType, AutomationsUpdateStatus>>
  >({});

  const releasePending = (automationType: AutomationType) => {
    const next = new Set(pendingRef.current);
    next.delete(automationType);
    pendingRef.current = next;
    setPending(next);
  };

  const mutation = useMutation<
    CreatorAutomationResponse,
    unknown,
    PatchVariables
  >({
    mutationFn: ({ automationType, body }) =>
      updateCreatorAutomation(creatorId, automationType, body),
    retry: false,
    onSuccess: (canonical, { automationType }) => {
      const row = toKnownAutomation(canonical);
      if (!row) return;
      queryClient.setQueryData<CreatorAutomationItem[]>(queryKey, (current) =>
        withRow(current, row),
      );
      setStatuses((current) => ({
        ...current,
        [automationType]: { kind: 'saved' },
      }));
    },
    /**
     * Rollback flips `enabled` back instead of restoring a snapshot, which
     * holds only because the optimistic patch in `patch` touches no other
     * field: config is never applied optimistically, the canonical row from
     * onSuccess is the first time the cache sees it.
     */
    onError: (error, { automationType, body }) => {
      if (body.enabled !== undefined) {
        const enabled = body.enabled;
        queryClient.setQueryData<CreatorAutomationItem[]>(queryKey, (current) =>
          withEnabled(current, automationType, !enabled),
        );
      }
      setErrors((current) => ({
        ...current,
        [automationType]: toUpdateError(error, body.enabled),
      }));
      setStatuses((current) => ({ ...current, [automationType]: undefined }));
    },
    onSettled: (_data, _error, { automationType }) =>
      releasePending(automationType),
  });

  const refetch = async () => {
    await query.refetch();
  };

  const data = toAsync(query, (error) => ({
    kind: loadErrorKindOf(error),
    retry: refetch,
  }));

  if (data.type !== 'fetched') return data;

  const patch = async (
    automationType: AutomationType,
    body: UpdateCreatorAutomationRequest,
  ) => {
    if (pendingRef.current.has(automationType)) return;
    const exists = data.value.some(
      (item) => item.automationType === automationType,
    );
    if (!exists) return;

    pendingRef.current = new Set(pendingRef.current).add(automationType);
    setPending(pendingRef.current);
    setStatuses((current) => ({
      ...current,
      [automationType]: { kind: 'saving' },
    }));
    setErrors((current) => ({ ...current, [automationType]: undefined }));
    if (body.enabled !== undefined) {
      const enabled = body.enabled;
      queryClient.setQueryData<CreatorAutomationItem[]>(queryKey, (current) =>
        withEnabled(current, automationType, enabled),
      );
    }

    await mutation.mutateAsync({ automationType, body }).catch(() => {
      // Outcome is recorded in `errors` by onError; the View reads it per row.
    });
  };

  return {
    type: 'fetched',
    value: {
      automations: data.value,
      pending,
      errors,
      statuses,
      retry: refetch,
      update: (automationType, enabled, config) =>
        patch(automationType, config ? { enabled, config } : { enabled }),
      saveConfig: (automationType, config) => patch(automationType, { config }),
    },
  };
};
